import React from "react";
import { ProposalContent, SavedProposal } from "@/types/proposal";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface EmailSummaryBoxProps {
  savedProposals: SavedProposal[];
  userId: string | undefined;
  clientName: string;
}

export const EmailSummaryBox: React.FC<EmailSummaryBoxProps> = ({ savedProposals, userId, clientName }) => {
  const [copied, setCopied] = React.useState(false);
  const [proposalContent, setProposalContent] = React.useState<ProposalContent | null>(null);
  const [loading, setLoading] = React.useState(false);

  const loadProposalData = async (proposalName: string) => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('saved_proposals')
        .select('content')
        .eq('user_id', userId)
        .eq('proposal_name', proposalName)
        .maybeSingle();
      
      if (error) throw error;
      
      if (data) {
        setProposalContent(data.content as any as ProposalContent);
      }
    } catch (error) {
      console.error('Failed to load proposal:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-select proposal based on client name
  React.useEffect(() => {
    const matchingProposal = savedProposals.find(p => 
      p.name.toLowerCase().includes(clientName.toLowerCase())
    );
    
    if (matchingProposal) {
      loadProposalData(matchingProposal.name);
    }
  }, [clientName, savedProposals, userId]);

  const generateSummary = () => {
    if (!proposalContent) return "Please select a proposal to generate summary.";
    
    const timelineText = proposalContent.timeline.map(t => `  - ${t.period}: ${t.task}`).join('\n');
    const nextStepsText = proposalContent.nextSteps.map((s, i) => `  ${i + 1}. ${s}`).join('\n');
    const optionsText = proposalContent.cabinetOptions.map(opt => `  - ${opt.title}: ${opt.description}`).join('\n');
    
    // Find matching proposal name for URL
    const matchingProposal = savedProposals.find(p => 
      p.name.toLowerCase().includes(clientName.toLowerCase())
    );
    const proposalUrl = matchingProposal && userId
      ? `${window.location.origin}/?client=true&proposal=${encodeURIComponent(matchingProposal.name)}&creator=${userId}`
      : '';

    return `PROPOSAL SUMMARY FOR EMAIL

Client Name: ${proposalContent.clientName}
Project: ${proposalContent.projectName}
Project Number: ${proposalContent.projectNumber}
Date: ${proposalContent.date}

${proposalUrl ? `VIEW PROPOSAL: ${proposalUrl}\n` : ''}
INVESTMENT & WARRANTY:
Total Investment: ${proposalContent.totalInvestment}
Warranty: ${proposalContent.warrantyYears}
Warranty Details: ${proposalContent.warrantyText}

EXECUTIVE SUMMARY:
${proposalContent.executiveSummary}

DESIGN OPTIONS (${proposalContent.numberOfOptions} option${proposalContent.numberOfOptions > 1 ? 's' : ''}):
${optionsText}

FLOOR PLAN:
${proposalContent.floorPlanDescription}
${proposalContent.onlinePlansLink ? `Online Plans: ${proposalContent.onlinePlansLink}` : ''}
${proposalContent.sketchupLink ? `3D Model: ${proposalContent.sketchupLink}` : ''}

PROJECT TIMELINE:
${timelineText}

NEXT STEPS:
${nextStepsText}

CLOSING NOTE:
${proposalContent.closingNote}

CONTACT INFO:
${proposalContent.companyAddress}
${proposalContent.companyPhone}
${proposalContent.companyEmail}
${proposalContent.companyWebsite}

Tagline: ${proposalContent.tagline}`;
  };

  const handleCopy = () => {
    const summary = generateSummary();
    navigator.clipboard.writeText(summary).then(() => {
      setCopied(true);
      toast.success("Email summary copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error("Failed to copy to clipboard");
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 mb-4 relative z-10">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800">Email Summary</h3>
        <Button 
          onClick={handleCopy}
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={!proposalContent || loading}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copy for Email
            </>
          )}
        </Button>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-700 whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
        {loading ? "Loading proposal data..." : generateSummary()}
      </div>
    </div>
  );
};
